import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Clock, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ScheduleGrid = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);

  const daysOfWeek = [
    { key: 'monday', label: 'Segunda' },
    { key: 'tuesday', label: 'Terça' },
    { key: 'wednesday', label: 'Quarta' },
    { key: 'thursday', label: 'Quinta' },
    { key: 'friday', label: 'Sexta' },
    { key: 'saturday', label: 'Sábado' },
    { key: 'sunday', label: 'Domingo' }
  ];

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      const response = await axios.get(`${API}/schedules`);
      setSchedules(response.data);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSchedulesForDay = (day) => {
    return schedules.filter(s => s.day_of_week === day).sort((a, b) => {
      const timeA = a.time_slot.split('-')[0];
      const timeB = b.time_slot.split('-')[0];
      return timeA.localeCompare(timeB);
    });
  };

  const allTimeSlots = [...new Set(schedules.map(s => s.time_slot))].sort();

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="animate-spin h-12 w-12 text-red-600" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      {/* Hero */}
      <section className="bg-gradient-to-r from-red-600 to-red-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4" data-testid="schedule-title">Grade de Horários</h1>
          <p className="text-xl">Confira os horários de treinos do Pequeninos do Jockey</p>
        </div>
      </section>

      {/* Schedule Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="overflow-x-auto">
              <div className="bg-white shadow-2xl rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full border-collapse min-w-[900px]">
                  <thead>
                    <tr className="bg-gradient-to-r from-red-600 to-red-700 text-white">
                      <th className="p-5 text-left font-bold border-r border-red-500 w-36">
                        <Clock className="inline mr-2" size={20} />
                        Horário
                      </th>
                      {daysOfWeek.map(day => (
                        <th key={day.key} className="p-5 text-center font-bold border-r border-red-500 last:border-r-0 text-sm md:text-base">
                          {day.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {allTimeSlots.map((timeSlot, index) => (
                      <tr key={timeSlot} className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-red-50 transition`}>
                        <td className="p-4 font-semibold border-r border-gray-300 text-gray-700">
                          <Clock className="inline mr-2 text-red-600" size={18} />
                          {timeSlot}
                        </td>
                        {daysOfWeek.map(day => {
                          const daySchedules = getSchedulesForDay(day.key).filter(s => s.time_slot === timeSlot);
                          return (
                            <td key={day.key} className="p-3 border-r border-gray-200 last:border-r-0 align-top min-w-[110px]">
                              {daySchedules.map(schedule => (
                                <div key={schedule.id} className="mb-2 p-3 bg-gradient-to-br from-red-100 to-red-50 rounded-lg hover:from-red-200 hover:to-red-100 transition border border-red-200">
                                  <div className="font-semibold text-red-800 mb-1">
                                    {schedule.categories.join(', ')}
                                  </div>
                                  {schedule.location && (
                                    <div className="text-xs text-gray-600">
                                      📍 {schedule.location}
                                    </div>
                                  )}
                                  {schedule.notes && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      {schedule.notes}
                                    </div>
                                  )}
                                </div>
                              ))}
                              {daySchedules.length === 0 && (
                                <div className="text-center text-gray-300 text-sm py-2">-</div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <Card className="mt-8">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Informações Importantes</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Os horários podem sofrer alterações em caso de chuva ou eventos especiais</li>
                <li>• Chegue com 15 minutos de antecedência</li>
                <li>• Traga garrafa de água e protetor solar</li>
                <li>• Em caso de dúvidas, entre em contato: (11) 95020-0214</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ScheduleGrid;